import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import AuthGuard from '../contexts/AuthGuard';

const Dashboard = lazy(() => import('../pages/modules/admin/dashboard/Dashboard'));
const FOS = lazy(() => import('../pages/modules/admin/user-management/FOS/FOS'));
const Retailers = lazy(() => import('../pages/modules/admin/user-management/Retailers/Retailers'));
const Restaurant = lazy(() => import('../pages/modules/admin/user-management/Restaurant/Restaurant'));
const BranchManagement = lazy(() => import('../pages/modules/admin/user-management/BranchManagement/BranchManagement'));
const KitchenManagement = lazy(() => import('../pages/modules/admin/user-management/KitchenManagement/KitchenManagement'));
const DeliveryManagement = lazy(() => import('../pages/modules/admin/user-management/DeliveryManagement/DeliveryManagement'));
const CashierManagement = lazy(() => import('../pages/modules/admin/user-management/CashierManagement/CashierManagement'));
const CustomerManagement = lazy(() => import('../pages/modules/admin/user-management/CustomerManagement/CustomerManagement'));
const MenuCategory = lazy(() => import('../pages/modules/admin/menu-management/MenuCategory/MenuCategory'));
const MenuSubcategory = lazy(() => import('../pages/modules/admin/menu-management/MenuSubcategory/MenuSubcategory'));
const Section = lazy(() => import('../pages/modules/admin/menu-management/Section/Section'));
const DiningTables = lazy(() => import('../pages/modules/admin/menu-management/DiningTables/DiningTables'));
const MenuItems = lazy(() => import('../pages/modules/admin/menu-management/MenuItems/MenuItems'));
const MenuItemsBulkUpdate = lazy(() => import('../pages/modules/admin/menu-management/MenuItems/MenuItemsBulkUpdate'));
const Addons = lazy(() => import('../pages/modules/admin/menu-management/Addons/Addons'));
const DeliveryZones = lazy(() => import('../pages/modules/admin/menu-management/DeliveryZones/DeliveryZones'));
const RestaurantHours = lazy(() => import('../pages/modules/admin/menu-management/RestaurantHours/RestaurantHours'));

// Order Management Components
const OrderList = lazy(() => import('../pages/modules/admin/orders/OrderList/OrderList'));
const NewOrders = lazy(() => import('../pages/modules/admin/orders/NewOrders/NewOrders'));
const Preparing = lazy(() => import('../pages/modules/admin/orders/Preparing/Preparing'));
const Onway = lazy(() => import('../pages/modules/admin/orders/Onway/Onway'));
const Delivered = lazy(() => import('../pages/modules/admin/orders/Delivered/Delivered'));
const CancelledOrders = lazy(() => import('../pages/modules/admin/orders/CancelledOrders/CancelledOrders'));

// Settings Components
const GlobalSetting = lazy(() => import('../pages/modules/admin/settings/GlobalSetting/GlobalSetting'));
const AppVersion = lazy(() => import('../pages/modules/admin/settings/AppVersion/AppVersion'));
const DeviceToken = lazy(() => import('../pages/modules/admin/settings/DeviceToken/DeviceToken'));
const PaymentGateway = lazy(() => import('../pages/modules/admin/settings/PaymentGateway/PaymentGateway'));
const State = lazy(() => import('../pages/modules/admin/settings/State/State'));
const City = lazy(() => import('../pages/modules/admin/settings/City/City'));
const ApiLogs = lazy(() => import('../pages/modules/admin/settings/ApiLogs/ApiLogs'));

// Shared operations pages (admin/branch/cashier)
const TablesManagement = lazy(() => import('../pages/modules/shared/TablesManagement/TablesManagement'));
const CustomerList = lazy(() => import('../pages/modules/shared/CustomerList/CustomerList'));

// SuperAdmin Components (merged)
const UserApprovals = lazy(() => import('../pages/modules/superadmin/user-approvals/UserApprovals'));
const UserDirectory = lazy(() => import('../pages/modules/superadmin/user-directory/UserDirectory'));
const SubscriptionPlans = lazy(() => import('../pages/modules/superadmin/subscription-plans/SubscriptionPlans'));
const Subscriptions = lazy(() => import('../pages/modules/superadmin/subscriptions/Subscriptions'));
const Coupons = lazy(() => import('../pages/modules/superadmin/coupons/Coupons'));
const TenantDomains = lazy(() => import('../pages/modules/superadmin/tenant-domains/TenantDomains'));
const SuperAdminSettings = lazy(() => import('../pages/modules/superadmin/settings/Settings'));

const RouteFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
  </div>
);

const AdminRoutes = () => {
  const { logout } = useAuth();

  return (
    <AuthGuard>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<AdminLayout onLogout={logout} />}>
          <Route path="/" element={<Navigate to="/superadmin/dashboard" replace />} />
          <Route path="/superadmin" element={<Navigate to="/superadmin/dashboard" replace />} />

          {/* Dashboard */}
          <Route path="/superadmin/dashboard" element={<Dashboard />} />

          {/* SuperAdmin specific routes (merged) */}
          <Route path="/superadmin/user-approvals" element={<UserApprovals />} />
          <Route path="/superadmin/user-directory" element={<UserDirectory />} />
          <Route path="/superadmin/subscription-plans" element={<SubscriptionPlans />} />
          <Route path="/superadmin/subscriptions" element={<Subscriptions />} />
          <Route path="/superadmin/coupons" element={<Coupons />} />
          <Route path="/superadmin/tenant-domains" element={<TenantDomains />} />
          <Route path="/superadmin/sa-settings" element={<SuperAdminSettings />} />

          {/* User Management */}
          <Route path="/superadmin/user-management/fos" element={<FOS />} />
          <Route path="/superadmin/user-management/retailers" element={<Retailers />} />
          <Route path="/superadmin/user-management/restaurants" element={<Restaurant />} />
          <Route path="/superadmin/user-management/branches" element={<BranchManagement />} />
          <Route path="/superadmin/user-management/kitchen" element={<KitchenManagement />} />
          <Route path="/superadmin/user-management/delivery" element={<DeliveryManagement />} />
          <Route path="/superadmin/user-management/cashier" element={<CashierManagement />} />
          <Route path="/superadmin/user-management/customers" element={<CustomerManagement />} />

          {/* Menu Management */}
          <Route path="/superadmin/menu-management/categories" element={<MenuCategory />} />
          <Route path="/superadmin/menu-management/subcategories" element={<MenuSubcategory />} />
          <Route path="/superadmin/menu-management/sections" element={<Section />} />
          <Route path="/superadmin/menu-management/dining-tables" element={<DiningTables />} />
          <Route path="/superadmin/menu-management/items" element={<MenuItems />} />
          <Route path="/superadmin/user-management/items-bulk-update" element={<MenuItemsBulkUpdate />} />
          <Route path="/superadmin/menu-management/items-bulk-update" element={<MenuItemsBulkUpdate />} />
          <Route path="/superadmin/menu-management/addons" element={<Addons />} />
          <Route path="/superadmin/user-management/delivery-zones" element={<DeliveryZones />} />
          <Route path="/superadmin/user-management/restaurant-hours" element={<RestaurantHours />} />

          {/* Order Management Routes */}
          <Route path="/superadmin/orders/list" element={<OrderList />} />
          <Route path="/superadmin/orders/new" element={<NewOrders />} />
          <Route path="/superadmin/orders/preparing" element={<Preparing />} />
          <Route path="/superadmin/orders/onway" element={<Onway />} />
          <Route path="/superadmin/orders/delivered" element={<Delivered />} />
          <Route path="/superadmin/orders/cancelled" element={<CancelledOrders />} />

          {/* Settings Routes */}
          <Route path="/superadmin/settings/global-setting" element={<GlobalSetting />} />
          <Route path="/superadmin/settings/app-version" element={<AppVersion />} />
          <Route path="/superadmin/settings/device-token" element={<DeviceToken />} />
          <Route path="/superadmin/settings/payment-gateway" element={<PaymentGateway />} />
          <Route path="/superadmin/settings/api-logs" element={<ApiLogs />} />
          <Route path="/superadmin/settings/state" element={<State />} />
          <Route path="/superadmin/settings/city" element={<City />} />

          {/* Operations — shared across admin/branch/cashier */}
          <Route path="/superadmin/operations/tables" element={<TablesManagement scope="admin" />} />
          <Route path="/superadmin/operations/customers" element={<CustomerList scope="admin" />} />

          <Route path="*" element={<Navigate to="/superadmin/dashboard" replace />} />
        </Route>
      </Routes>
      </Suspense>
    </AuthGuard>
  );
};

export default AdminRoutes;
